import { tokenSelector } from 'src/state/authentication/selectors'
import store from 'src/state/store'
import HttpError from './HttpError'

type AnyObject = Blob | Record<string, unknown | null>

export type Data = {
  headers?: Record<string, string>
  keepalive?: boolean
  params?: AnyObject
  url: string
  token?: boolean
}

export interface ExtendedData extends Data {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
}

type FetchReturn = Promise<any>

type Middlewares = {
  onRequest: {
    (data: ExtendedData): Promise<ExtendedData>
  }[]
  onResponse: {
    (response: AnyObject): Promise<AnyObject>
  }[]
  onError: { (err: HttpError): Promise<unknown> }[]
  onComplete: { (data: ExtendedData, succeeded: boolean): void }[]
}

const headerJsonType = ['application/json', 'text/plain', 'text/html', 'text/html; charset=UTF-8']

export class Http {
  middlewares: Middlewares = {
    onRequest: [],
    onResponse: [],
    onError: [],
    onComplete: []
  }

  get(data: Data): FetchReturn {
    const extendedData: ExtendedData = {
      ...data,
      url: data.params
        ? `${data.url}?${new URLSearchParams(
            Object.entries(data.params).reduce((acc, [key, value]) => {
              if (
                value !== null &&
                value !== undefined &&
                value !== '' &&
                (!Array.isArray(value) ||
                  (value.length > 0 && !(value.length === 1 && value[0] === 0)))
              ) {
                acc[key] = Array.isArray(value) ? value.join(',') : String(value)
              }
              return acc
            }, {} as Record<string, string>)
          ).toString()}`
        : data.url,
      method: 'GET'
    }
    return this.onRequestMiddlewares(extendedData).then(requestData => {
      return this.makeRequest(requestData)
    })
  }

  delete(data: Data): FetchReturn {
    const extendedData: ExtendedData = {
      ...data,
      method: 'DELETE'
    }

    return this.onRequestMiddlewares(extendedData).then(requestData => {
      return this.makeRequest(requestData)
    })
  }

  post(data: Data): FetchReturn {
    const extendedData: ExtendedData = { ...data, method: 'POST' }

    return this.onRequestMiddlewares(extendedData).then(requestData => {
      return this.makeRequest(requestData)
    })
  }

  makeRequest({ method, params, headers, url, keepalive = false }: ExtendedData): FetchReturn {
    if (headers) {
      const currentToken = headers.Authorization.replace('Bearer ', '')
      const storeToken = tokenSelector(store.getState())
      if (storeToken && currentToken !== storeToken) {
        headers.Authorization = `Bearer ${storeToken}`
      }
    }
    const req = fetch(url, {
      method,
      headers,
      keepalive,
      ...(method !== 'GET' && { body: this.serializeBody(params) })
    }).then(this.processResponse)

    const result = this.attachMiddlewares(req)
    result.then(
      () => this.notifyComplete({ method, params, headers, url, keepalive }, true),
      () => this.notifyComplete({ method, params, headers, url, keepalive }, false)
    )
    return result
  }

  protected serializeBody(params?: AnyObject): BodyInit | undefined {
    return JSON.stringify(params)
  }

  onRequestMiddlewares(data: ExtendedData): Promise<ExtendedData> {
    return this.middlewares.onRequest.reduce(
      (promise, midleware) => promise.then(midleware),
      Promise.resolve(data)
    )
  }

  notifyComplete(data: ExtendedData, succeeded: boolean): void {
    this.middlewares.onComplete.forEach(middleware => middleware(data, succeeded))
  }

  attachMiddlewares(req: Promise<AnyObject>): FetchReturn {
    let extendedReq = this.middlewares.onResponse.reduce(
      (promise, midleware) => promise.then(midleware),
      Promise.resolve(req)
    )

    extendedReq = this.middlewares.onError.reduce(
      (promise, middleware) =>
        promise.catch(err => {
          middleware(err)
          throw err
        }),
      Promise.resolve(extendedReq)
    )

    return extendedReq
  }

  async processResponse(response: Response): Promise<AnyObject> {
    if (response.status >= 400) {
      const error = await response.json()
      throw new HttpError(error, response.status)
    }
    if (response.status === 204) {
      return {}
    }
    if (!headerJsonType.includes(response.headers.get('Content-Type') as string)) {
      return response.blob()
    }
    return response.json()
  }

  addMidleware<K extends keyof Middlewares>(type: K, midleware: Middlewares[K][number]): void {
    // Any used because of https://github.com/microsoft/TypeScript/issues/31663
    this.middlewares[type].push(midleware as any)
  }
}
